<template>
    <svg
      :viewBox="viewBox"
      xmlns="http://www.w3.org/2000/svg"
      class="schematic-svg"
      :class="{ 'animations-paused': animationsPaused }"
      preserveAspectRatio="xMidYMid meet"
      aria-label="Pumping system schematic"
      role="img"
    >
      <!-- ── Pipe backbone ── -->
      <!-- Suction side (before pump) = thicker; discharge side = thinner -->
      <template v-for="seg in pipeSegments" :key="seg.id">
        <line
          :x1="seg.x1"
          :y1="PIPE_Y"
          :x2="seg.x2"
          :y2="PIPE_Y"
          stroke="#94a3b8"
          :stroke-width="seg.suction ? 18 : 10"
          stroke-linecap="round"
        />
        <line
          :x1="seg.x1"
          :y1="PIPE_Y"
          :x2="seg.x2"
          :y2="PIPE_Y"
          stroke="rgba(59,130,246,0.65)"
          :stroke-width="seg.suction ? 9 : 5"
          stroke-linecap="round"
          stroke-dasharray="16 12"
          class="flow-dash"
        />
      </template>

      <!-- ── Diagonal discharge pipe (45°) to destination tank ── -->
      <line
        :x1="riseStartX"
        :y1="PIPE_Y"
        :x2="riseEndX"
        :y2="riseEndY"
        stroke="#94a3b8"
        stroke-width="10"
        stroke-linecap="round"
      />
      <line
        :x1="riseStartX"
        :y1="PIPE_Y"
        :x2="riseEndX"
        :y2="riseEndY"
        stroke="rgba(59,130,246,0.65)"
        stroke-width="5"
        stroke-linecap="round"
        stroke-dasharray="16 12"
        class="flow-dash flow-dash--up"
      />
      <!-- horizontal connector from last slot to diagonal start -->
      <line
        v-if="lastSlotRight < riseStartX"
        :x1="lastSlotRight"
        :y1="PIPE_Y"
        :x2="riseStartX"
        :y2="PIPE_Y"
        stroke="#94a3b8"
        stroke-width="10"
        stroke-linecap="round"
      />
      <line
        v-if="lastSlotRight < riseStartX"
        :x1="lastSlotRight"
        :y1="PIPE_Y"
        :x2="riseStartX"
        :y2="PIPE_Y"
        stroke="rgba(59,130,246,0.65)"
        stroke-width="5"
        stroke-linecap="round"
        stroke-dasharray="16 12"
        class="flow-dash"
      />

      <!-- ── Diagonal slots (pipes + fittings on the incline) ── -->
      <template v-for="dslot in diagonalLayout" :key="dslot.id">
        <g
          :transform="`translate(${dslot.cx},${dslot.cy}) rotate(-45)`"
          role="button"
          tabindex="0"
          :aria-label="`${dslot.label} slot, ${slotAssignments[dslot.id] ? 'filled' : 'empty'}`"
          @click="$emit('select-slot', dslot.id)"
          @keydown.enter.prevent="$emit('select-slot', dslot.id)"
          @keydown.space.prevent="$emit('select-slot', dslot.id)"
          class="cursor-pointer"
        >
          <!-- Hit area -->
          <rect
            :x="dslot.accepts === 'pipe' ? -50 : -28"
            :y="-18"
            :width="dslot.accepts === 'pipe' ? 100 : 56"
            height="36"
            rx="7"
            :fill="
              activeSlot === dslot.id ? 'rgba(59,130,246,0.10)' : 'transparent'
            "
            :stroke="
              activeSlot === dslot.id
                ? '#3b82f6'
                : slotAssignments[dslot.id]
                  ? '#64748b'
                  : '#cbd5e1'
            "
            :stroke-dasharray="slotAssignments[dslot.id] ? 'none' : '5 3'"
            stroke-width="1.5"
          />
          <!-- Pipe visual -->
          <template v-if="dslot.accepts === 'pipe'">
            <rect
              x="-42"
              y="-7"
              width="84"
              height="14"
              rx="7"
              :fill="slotAssignments[dslot.id] ? '#93c5fd' : '#e2e8f0'"
              :stroke="slotAssignments[dslot.id] ? '#1d4ed8' : '#94a3b8'"
              stroke-width="1.5"
              pointer-events="none"
            />
          </template>
          <!-- Fitting visual -->
          <template v-else-if="dslot.accepts === 'fitting'">
            <rect
              x="-18"
              y="-14"
              width="36"
              height="28"
              rx="12"
              :fill="slotAssignments[dslot.id] ? '#fde68a' : '#f1f5f9'"
              :stroke="slotAssignments[dslot.id] ? '#d97706' : '#94a3b8'"
              stroke-width="2"
              pointer-events="none"
            />
            <text
              y="5"
              text-anchor="middle"
              font-size="14"
              :fill="slotAssignments[dslot.id] ? '#92400e' : '#94a3b8'"
              font-family="sans-serif"
              font-weight="700"
              pointer-events="none"
            >
              ⌐
            </text>
          </template>
          <!-- Label (rotated back to horizontal) -->
          <text
            :y="dslot.accepts === 'pipe' ? 32 : 30"
            text-anchor="middle"
            font-size="11"
            :fill="slotAssignments[dslot.id] ? '#1e293b' : '#475569'"
            font-family="sans-serif"
            font-weight="600"
            pointer-events="none"
            transform="rotate(45)"
          >
            {{
              slotAssignments[dslot.id]
                ? slotAssignments[dslot.id].name
                : dslot.label
            }}
          </text>
          <!-- Sub-detail -->
          <text
            v-if="slotAssignments[dslot.id] && dslot.accepts === 'pipe'"
            :y="46"
            text-anchor="middle"
            font-size="10"
            fill="#475569"
            font-family="sans-serif"
            pointer-events="none"
            transform="rotate(45)"
          >
            {{ slotAssignments[dslot.id].selectedSize?.nominal }}" &bull;
            {{ slotAssignments[dslot.id].length_ft }} ft
          </text>
          <text
            v-if="slotAssignments[dslot.id] && dslot.accepts === 'fitting'"
            :y="44"
            text-anchor="middle"
            font-size="10"
            fill="#475569"
            font-family="sans-serif"
            pointer-events="none"
            transform="rotate(45)"
          >
            {{ slotAssignments[dslot.id].material || "" }} • K={{
              slotAssignments[dslot.id].K
            }}
          </text>
          <!-- Empty hint -->
          <text
            v-if="!slotAssignments[dslot.id]"
            :y="dslot.accepts === 'pipe' ? 46 : 44"
            text-anchor="middle"
            font-size="10"
            fill="#64748b"
            font-family="sans-serif"
            pointer-events="none"
            transform="rotate(45)"
          >
            click to assign
          </text>
          <!-- Clear × -->
          <g
            v-if="slotAssignments[dslot.id]"
            @click.stop="$emit('clear-slot', dslot.id)"
            @keydown.enter.stop.prevent="$emit('clear-slot', dslot.id)"
            @keydown.space.stop.prevent="$emit('clear-slot', dslot.id)"
            role="button"
            tabindex="0"
            :aria-label="
              'Remove ' + (slotAssignments[dslot.id]?.name || dslot.label)
            "
            class="cursor-pointer"
          >
            <circle
              :cx="dslot.accepts === 'pipe' ? 48 : 24"
              cy="-12"
              r="9"
              fill="white"
              stroke="#94a3b8"
              stroke-width="1"
            />
            <text
              :x="dslot.accepts === 'pipe' ? 48 : 24"
              y="-8"
              text-anchor="middle"
              font-size="13"
              fill="#6b7280"
              font-family="sans-serif"
            >
              ×
            </text>
          </g>
        </g>
      </template>

      <!-- ── Elevation annotation ── -->
      <g v-if="elevPx > 20">
        <!-- vertical dashed line at diagonal end -->
        <line
          :x1="riseEndX + 16"
          :y1="PIPE_Y"
          :x2="riseEndX + 16"
          :y2="riseEndY"
          stroke="#bbb"
          stroke-width="1"
          stroke-dasharray="4 3"
        />
        <line
          :x1="riseEndX + 8"
          :y1="PIPE_Y"
          :x2="riseEndX + 24"
          :y2="PIPE_Y"
          stroke="#bbb"
          stroke-width="1"
        />
        <line
          :x1="riseEndX + 8"
          :y1="riseEndY"
          :x2="riseEndX + 24"
          :y2="riseEndY"
          stroke="#bbb"
          stroke-width="1"
        />
        <!-- arrow tips -->
        <polygon
          :points="`${riseEndX + 12},${PIPE_Y - 6} ${riseEndX + 20},${PIPE_Y - 6} ${riseEndX + 16},${PIPE_Y - 1}`"
          fill="#bbb"
        />
        <polygon
          :points="`${riseEndX + 12},${riseEndY + 6} ${riseEndX + 20},${riseEndY + 6} ${riseEndX + 16},${riseEndY + 1}`"
          fill="#bbb"
        />
        <text
          :x="riseEndX + 30"
          :y="(PIPE_Y + riseEndY) / 2 + 4"
          font-size="11"
          fill="#64748b"
          font-family="sans-serif"
        >
          +{{ elevationChange_ft }} ft
        </text>
      </g>

      <!-- ── Suction drop pipe from elevated source tank to pipe level ── -->
      <line
        :x1="SRC_X + TANK_W / 2"
        :y1="srcY + TANK_H"
        :x2="SRC_X + TANK_W / 2"
        :y2="PIPE_Y"
        stroke="#94a3b8"
        stroke-width="18"
        stroke-linecap="round"
      />
      <line
        :x1="SRC_X + TANK_W / 2"
        :y1="srcY + TANK_H"
        :x2="SRC_X + TANK_W / 2"
        :y2="PIPE_Y"
        stroke="rgba(59,130,246,0.65)"
        stroke-width="9"
        stroke-linecap="round"
        stroke-dasharray="16 12"
        class="flow-dash flow-dash--down"
      />

      <!-- ── Source tank ── -->
      <rect
        :x="SRC_X"
        :y="srcY"
        :width="TANK_W"
        :height="TANK_H"
        rx="6"
        fill="#dbeafe"
        stroke="#1d4ed8"
        stroke-width="2.5"
      />
      <path
        :d="`M${SRC_X + 8} ${srcY + TANK_H - 22} q${TANK_W / 4}-9 ${TANK_W / 2} 0 q${TANK_W / 4} 9 ${TANK_W / 4} 0`"
        fill="none"
        stroke="#1d4ed8"
        stroke-width="2"
      />
      <text
        :x="SRC_X + TANK_W / 2"
        :y="srcY + 28"
        text-anchor="middle"
        font-size="13"
        fill="#1e3a8a"
        font-weight="600"
        font-family="sans-serif"
      >
        SOURCE
      </text>
      <text
        :x="SRC_X + TANK_W / 2"
        :y="srcY + 44"
        text-anchor="middle"
        font-size="10"
        fill="#1e40af"
        font-family="sans-serif"
      >
        <tspan :x="SRC_X + TANK_W / 2" dy="0">{{ fluidLines[0] }}</tspan>
        <tspan v-if="fluidLines[1]" :x="SRC_X + TANK_W / 2" :dy="FLUID_LINE_H">
          {{ fluidLines[1] }}
        </tspan>
      </text>
      <text
        :x="SRC_X + TANK_W / 2"
        :y="srcY + 58"
        text-anchor="middle"
        font-size="10"
        fill="#334155"
        font-family="sans-serif"
      >
        <tspan :x="SRC_X + TANK_W / 2" :dy="fluidLines[1] ? FLUID_LINE_H : 0">
          z₁ = +{{ sourceLevel_ft }} ft
        </tspan>
      </text>

      <!-- ── Top-level horizontal pipe (fittings run, discharge side) ── -->
      <template v-for="seg in topPipeSegments" :key="seg.id">
        <line
          :x1="seg.x1"
          :y1="riseEndY"
          :x2="seg.x2"
          :y2="riseEndY"
          stroke="#94a3b8"
          stroke-width="10"
          stroke-linecap="round"
        />
        <line
          :x1="seg.x1"
          :y1="riseEndY"
          :x2="seg.x2"
          :y2="riseEndY"
          stroke="rgba(59,130,246,0.65)"
          stroke-width="5"
          stroke-linecap="round"
          stroke-dasharray="16 12"
          class="flow-dash"
        />
      </template>

      <!-- ── Top-level slots (fittings) ── -->
      <template v-for="slot in topLayout" :key="slot.id">
        <rect
          :x="slot.cx - slot.hw - 4"
          :y="riseEndY - slot.hh - 4"
          :width="(slot.hw + 4) * 2"
          :height="(slot.hh + 4) * 2"
          rx="7"
          :fill="
            activeSlot === slot.id ? 'rgba(59,130,246,0.10)' : 'transparent'
          "
          :stroke="
            activeSlot === slot.id
              ? '#3b82f6'
              : slotAssignments[slot.id]
                ? '#64748b'
                : '#cbd5e1'
          "
          :stroke-dasharray="slotAssignments[slot.id] ? 'none' : '5 3'"
          stroke-width="1.5"
          role="button"
          tabindex="0"
          :aria-label="`${slot.label} slot, ${slotAssignments[slot.id] ? 'filled' : 'empty'}`"
          @click="$emit('select-slot', slot.id)"
          @keydown.enter.prevent="$emit('select-slot', slot.id)"
          @keydown.space.prevent="$emit('select-slot', slot.id)"
          class="cursor-pointer"
        />
        <g
          :transform="`translate(${slot.cx},${riseEndY})`"
          role="button"
          tabindex="0"
          :aria-label="`${slot.label} slot, ${slotAssignments[slot.id] ? 'filled' : 'empty'}`"
          @click="$emit('select-slot', slot.id)"
          @keydown.enter.prevent="$emit('select-slot', slot.id)"
          @keydown.space.prevent="$emit('select-slot', slot.id)"
          class="cursor-pointer"
        >
          <!-- Fitting visual -->
          <rect
            x="-18"
            y="-14"
            width="36"
            height="28"
            rx="12"
            :fill="slotAssignments[slot.id] ? '#fde68a' : '#f1f5f9'"
            :stroke="slotAssignments[slot.id] ? '#d97706' : '#94a3b8'"
            stroke-width="2"
            pointer-events="none"
          />
          <text
            y="5"
            text-anchor="middle"
            font-size="14"
            :fill="slotAssignments[slot.id] ? '#92400e' : '#94a3b8'"
            font-family="sans-serif"
            font-weight="700"
            pointer-events="none"
          >
            ⌐
          </text>
          <!-- Label -->
          <text
            :y="slot.hh + 18"
            text-anchor="middle"
            font-size="11"
            :fill="slotAssignments[slot.id] ? '#1e293b' : '#475569'"
            font-family="sans-serif"
            font-weight="600"
            pointer-events="none"
          >
            {{
              slotAssignments[slot.id]
                ? slotAssignments[slot.id].name
                : slot.label
            }}
          </text>
          <!-- Sub-detail -->
          <text
            v-if="slotAssignments[slot.id]"
            :y="slot.hh + 32"
            text-anchor="middle"
            font-size="10"
            fill="#475569"
            font-family="sans-serif"
            pointer-events="none"
          >
            {{ slotAssignments[slot.id].material || "" }} • K={{
              slotAssignments[slot.id].K
            }}
          </text>
          <!-- Empty hint -->
          <text
            v-if="!slotAssignments[slot.id]"
            :y="slot.hh + 32"
            text-anchor="middle"
            font-size="10"
            fill="#64748b"
            font-family="sans-serif"
            pointer-events="none"
          >
            click to assign
          </text>
          <!-- Clear × -->
          <g
            v-if="slotAssignments[slot.id]"
            @click.stop="$emit('clear-slot', slot.id)"
            @keydown.enter.stop.prevent="$emit('clear-slot', slot.id)"
            @keydown.space.stop.prevent="$emit('clear-slot', slot.id)"
            role="button"
            tabindex="0"
            :aria-label="
              'Remove ' + (slotAssignments[slot.id]?.name || slot.label)
            "
            class="cursor-pointer"
          >
            <circle
              :cx="slot.hw + 2"
              :cy="-slot.hh - 2"
              r="9"
              fill="white"
              stroke="#94a3b8"
              stroke-width="1"
            />
            <text
              :x="slot.hw + 2"
              :y="-slot.hh + 2"
              text-anchor="middle"
              font-size="13"
              fill="#6b7280"
              font-family="sans-serif"
            >
              ×
            </text>
          </g>
        </g>
      </template>

      <!-- ── Destination tank (elevated, at end of top run) ── -->
      <rect
        :x="destTankX"
        :y="DEST_Y"
        :width="TANK_W"
        :height="TANK_H"
        rx="6"
        fill="#d1fae5"
        stroke="#047857"
        stroke-width="2.5"
      />
      <path
        :d="`M${destTankX + 8} ${DEST_Y + TANK_H - 22} q${TANK_W / 4}-9 ${TANK_W / 2} 0 q${TANK_W / 4} 9 ${TANK_W / 4} 0`"
        fill="none"
        stroke="#047857"
        stroke-width="2"
      />
      <text
        :x="destTankX + TANK_W / 2"
        :y="DEST_Y + 28"
        text-anchor="middle"
        font-size="13"
        fill="#064e3b"
        font-weight="600"
        font-family="sans-serif"
      >
        DEST.
      </text>
      <text
        :x="destTankX + TANK_W / 2"
        :y="DEST_Y + 44"
        text-anchor="middle"
        font-size="10"
        fill="#065f46"
        font-family="sans-serif"
      >
        <tspan :x="destTankX + TANK_W / 2" dy="0">{{ fluidLines[0] }}</tspan>
        <tspan
          v-if="fluidLines[1]"
          :x="destTankX + TANK_W / 2"
          :dy="FLUID_LINE_H"
        >
          {{ fluidLines[1] }}
        </tspan>
      </text>
      <text
        :x="destTankX + TANK_W / 2"
        :y="DEST_Y + 58"
        text-anchor="middle"
        font-size="10"
        fill="#334155"
        font-family="sans-serif"
      >
        <tspan
          :x="destTankX + TANK_W / 2"
          :dy="fluidLines[1] ? FLUID_LINE_H : 0"
        >
          z₂ = +{{ elevationChange_ft }} ft
        </tspan>
      </text>

      <!-- ── Slot components ── -->
      <template v-for="slot in slotLayout" :key="slot.id">
        <!-- Hit / highlight rect -->
        <rect
          :x="slot.cx - slot.hw - 4"
          :y="PIPE_Y - slot.hh - 4"
          :width="(slot.hw + 4) * 2"
          :height="(slot.hh + 4) * 2"
          rx="7"
          :fill="
            activeSlot === slot.id ? 'rgba(59,130,246,0.10)' : 'transparent'
          "
          :stroke="
            activeSlot === slot.id
              ? '#3b82f6'
              : slotAssignments[slot.id]
                ? '#64748b'
                : '#cbd5e1'
          "
          :stroke-dasharray="slotAssignments[slot.id] ? 'none' : '5 3'"
          stroke-width="1.5"
          role="button"
          tabindex="0"
          :aria-label="`${slot.accepts === 'pipe' ? 'Pipe' : slot.accepts === 'pump' ? 'Pump' : 'Component'} slot ${slot.id}, ${slotAssignments[slot.id] ? 'filled' : 'empty'}`"
          @click="$emit('select-slot', slot.id)"
          @keydown.enter.prevent="$emit('select-slot', slot.id)"
          @keydown.space.prevent="$emit('select-slot', slot.id)"
          class="cursor-pointer"
        />

        <g
          :transform="`translate(${slot.cx},${PIPE_Y})`"
          role="button"
          tabindex="0"
          :aria-label="`${slot.accepts === 'pipe' ? 'Pipe' : slot.accepts === 'pump' ? 'Pump' : 'Component'} slot ${slot.id}, ${slotAssignments[slot.id] ? 'filled' : 'empty'}`"
          @click="$emit('select-slot', slot.id)"
          @keydown.enter.prevent="$emit('select-slot', slot.id)"
          @keydown.space.prevent="$emit('select-slot', slot.id)"
          class="cursor-pointer"
        >
          <title>
            {{
              `${slot.accepts === "pipe" ? "Pipe" : slot.accepts === "pump" ? "Pump" : "Component"} slot ${slot.id}, ${slotAssignments[slot.id] ? "filled" : "empty"}`
            }}
          </title>
          <!-- ── PIPE ── -->
          <template v-if="slot.accepts === 'pipe'">
            <rect
              x="-28"
              y="-7"
              width="56"
              height="14"
              rx="7"
              :fill="slotAssignments[slot.id] ? '#93c5fd' : '#e2e8f0'"
              :stroke="slotAssignments[slot.id] ? '#1d4ed8' : '#94a3b8'"
              stroke-width="1.5"
              pointer-events="none"
            />
          </template>

          <!-- ── PUMP ── -->
          <template v-else-if="slot.accepts === 'pump'">
            <circle
              r="34"
              :fill="slotAssignments[slot.id] ? '#eff6ff' : '#f8fafc'"
              :stroke="slotAssignments[slot.id] ? '#3b82f6' : '#94a3b8'"
              stroke-width="2.5"
              pointer-events="none"
            />
            <g
              :stroke="slotAssignments[slot.id] ? '#3b82f6' : '#94a3b8'"
              stroke-width="2"
              fill="none"
              pointer-events="none"
            >
              <line x1="0" y1="-24" x2="0" y2="-32" />
              <line x1="17" y1="-17" x2="23" y2="-23" />
              <line x1="24" y1="0" x2="32" y2="0" />
              <line x1="17" y1="17" x2="23" y2="23" />
              <line x1="0" y1="24" x2="0" y2="32" />
              <line x1="-17" y1="17" x2="-23" y2="23" />
              <line x1="-24" y1="0" x2="-32" y2="0" />
              <line x1="-17" y1="-17" x2="-23" y2="-23" />
            </g>
            <circle
              r="11"
              :fill="slotAssignments[slot.id] ? '#dbeafe' : '#f1f5f9'"
              :stroke="slotAssignments[slot.id] ? '#3b82f6' : '#94a3b8'"
              stroke-width="1.5"
              pointer-events="none"
            />
            <line
              x1="0"
              y1="-11"
              x2="0"
              y2="0"
              :stroke="slotAssignments[slot.id] ? '#3b82f6' : '#94a3b8'"
              stroke-width="2"
              stroke-linecap="round"
              pointer-events="none"
            />
            <line
              x1="0"
              y1="0"
              x2="10"
              y2="5"
              :stroke="slotAssignments[slot.id] ? '#3b82f6' : '#94a3b8'"
              stroke-width="2"
              stroke-linecap="round"
              pointer-events="none"
            />
            <circle
              r="3"
              :fill="slotAssignments[slot.id] ? '#3b82f6' : '#94a3b8'"
              pointer-events="none"
            />
          </template>

          <!-- ── VALVE ── -->
          <template v-else-if="slot.accepts === 'valve'">
            <!-- Flanges -->
            <rect
              x="-42"
              y="-12"
              width="14"
              height="24"
              rx="3"
              fill="#9ca3af"
              stroke="#6b7280"
              stroke-width="1"
              pointer-events="none"
            />
            <rect
              x="28"
              y="-12"
              width="14"
              height="24"
              rx="3"
              fill="#9ca3af"
              stroke="#6b7280"
              stroke-width="1"
              pointer-events="none"
            />
            <!-- Body -->
            <rect
              x="-30"
              y="-22"
              width="60"
              height="44"
              rx="5"
              :fill="slotAssignments[slot.id] ? '#c7d2fe' : '#e2e8f0'"
              :stroke="slotAssignments[slot.id] ? '#4338ca' : '#94a3b8'"
              stroke-width="1.5"
              pointer-events="none"
            />
            <!-- Gate disc -->
            <rect
              x="-22"
              y="-8"
              width="44"
              height="14"
              rx="2"
              fill="#6b7280"
              stroke="#374151"
              stroke-width="1"
              pointer-events="none"
            />
            <!-- Bonnet -->
            <rect
              x="-10"
              y="-44"
              width="20"
              height="24"
              rx="3"
              :fill="slotAssignments[slot.id] ? '#c7d2fe' : '#e2e8f0'"
              :stroke="slotAssignments[slot.id] ? '#4338ca' : '#94a3b8'"
              stroke-width="1.5"
              pointer-events="none"
            />
            <!-- Stem -->
            <line
              x1="0"
              y1="-68"
              x2="0"
              y2="-44"
              stroke="#9ca3af"
              stroke-width="5"
              stroke-linecap="round"
              pointer-events="none"
            />
            <!-- Handwheel -->
            <circle
              cy="-70"
              r="22"
              :fill="slotAssignments[slot.id] ? '#e0e7ff' : '#f1f5f9'"
              :stroke="slotAssignments[slot.id] ? '#6366f1' : '#94a3b8'"
              stroke-width="3.5"
              pointer-events="none"
            />
            <line
              x1="-22"
              y1="-70"
              x2="22"
              y2="-70"
              :stroke="slotAssignments[slot.id] ? '#6366f1' : '#94a3b8'"
              stroke-width="2.5"
              pointer-events="none"
            />
            <line
              x1="0"
              y1="-92"
              x2="0"
              y2="-48"
              :stroke="slotAssignments[slot.id] ? '#6366f1' : '#94a3b8'"
              stroke-width="2.5"
              pointer-events="none"
            />
            <circle
              cx="22"
              cy="-70"
              r="5"
              :fill="slotAssignments[slot.id] ? '#6366f1' : '#94a3b8'"
              pointer-events="none"
            />
          </template>

          <!-- ── FITTING ── -->
          <template v-else-if="slot.accepts === 'fitting'">
            <rect
              x="-18"
              y="-14"
              width="36"
              height="28"
              rx="12"
              :fill="slotAssignments[slot.id] ? '#fde68a' : '#f1f5f9'"
              :stroke="slotAssignments[slot.id] ? '#d97706' : '#94a3b8'"
              stroke-width="2"
              pointer-events="none"
            />
            <text
              y="5"
              text-anchor="middle"
              font-size="14"
              :fill="slotAssignments[slot.id] ? '#92400e' : '#94a3b8'"
              font-family="sans-serif"
              font-weight="700"
              pointer-events="none"
            >
              ⌐
            </text>
          </template>

          <!-- Label -->
          <text
            :y="slot.hh + 18"
            text-anchor="middle"
            font-size="11"
            :fill="slotAssignments[slot.id] ? '#1e293b' : '#475569'"
            font-family="sans-serif"
            font-weight="600"
            pointer-events="none"
          >
            {{
              slotAssignments[slot.id]
                ? slotAssignments[slot.id].name
                : slot.label
            }}
          </text>

          <!-- Sub-detail -->
          <text
            v-if="slotAssignments[slot.id] && slot.accepts === 'pump'"
            :y="slot.hh + 32"
            text-anchor="middle"
            font-size="10"
            fill="#3b82f6"
            font-family="sans-serif"
            pointer-events="none"
          >
            {{ slotAssignments[slot.id].hp }} HP
          </text>
          <text
            v-if="slotAssignments[slot.id] && slot.accepts === 'pipe'"
            :y="slot.hh + 32"
            text-anchor="middle"
            font-size="10"
            fill="#475569"
            font-family="sans-serif"
            pointer-events="none"
          >
            {{ slotAssignments[slot.id].selectedSize?.nominal }}" &bull;
            {{ slotAssignments[slot.id].length_ft }} ft
          </text>
          <text
            v-if="
              slotAssignments[slot.id] &&
              (slot.accepts === 'valve' || slot.accepts === 'fitting')
            "
            :y="slot.hh + 32"
            text-anchor="middle"
            font-size="10"
            fill="#475569"
            font-family="sans-serif"
            pointer-events="none"
          >
            {{
              slotAssignments[slot.id].material
                ? slotAssignments[slot.id].material + " • "
                : ""
            }}K={{ slotAssignments[slot.id].K }}
          </text>

          <!-- Empty hint -->
          <text
            v-if="!slotAssignments[slot.id]"
            :y="slot.hh + 32"
            text-anchor="middle"
            font-size="10"
            fill="#64748b"
            font-family="sans-serif"
            pointer-events="none"
          >
            click to assign
          </text>

          <!-- Clear × -->
          <g
            v-if="slotAssignments[slot.id]"
            @click.stop="$emit('clear-slot', slot.id)"
            @keydown.enter.stop.prevent="$emit('clear-slot', slot.id)"
            @keydown.space.stop.prevent="$emit('clear-slot', slot.id)"
            role="button"
            tabindex="0"
            :aria-label="
              'Remove ' + (slotAssignments[slot.id]?.name || slot.label)
            "
            class="cursor-pointer"
          >
            <circle
              :cx="slot.hw + 2"
              :cy="-slot.hh - 2"
              r="9"
              fill="white"
              stroke="#94a3b8"
              stroke-width="1"
            />
            <text
              :x="slot.hw + 2"
              :y="-slot.hh + 2"
              text-anchor="middle"
              font-size="13"
              fill="#6b7280"
              font-family="sans-serif"
            >
              ×
            </text>
          </g>

          <!-- Pop-in flash when newly assigned -->
          <rect
            v-if="lastAssignedSlot === slot.id && !animationsPaused"
            :x="-slot.hw - 4"
            :y="-slot.hh - 4"
            :width="(slot.hw + 4) * 2"
            :height="(slot.hh + 4) * 2"
            rx="7"
            fill="rgba(59,130,246,0.30)"
            class="slot-pop-flash"
            pointer-events="none"
            @animationend="$emit('clear-last-assigned')"
          />
        </g>
      </template>
    </svg>
</template>
<script>
export default {
  name: "PumpingDesignSchematic",
  props: {
    animationsPaused: { type: Boolean, default: false },
    viewBox: { type: String, required: true },
    pipeSegments: { type: Array, required: true },
    PIPE_Y: { type: Number, required: true },
    riseStartX: { type: Number, required: true },
    riseEndX: { type: Number, required: true },
    riseEndY: { type: Number, required: true },
    lastSlotRight: { type: Number, required: true },
    diagonalLayout: { type: Array, required: true },
    elevPx: { type: Number, required: true },
    elevationChange_ft: { type: [String, Number], required: true },
    SRC_X: { type: Number, required: true },
    TANK_W: { type: Number, required: true },
    TANK_H: { type: Number, required: true },
    srcY: { type: Number, required: true },
    fluidLines: { type: Array, required: true },
    FLUID_LINE_H: { type: Number, required: true },
    sourceLevel_ft: { type: Number, required: true },
    topPipeSegments: { type: Array, required: true },
    topLayout: { type: Array, required: true },
    slotAssignments: { type: Object, required: true },
    activeSlot: { type: String, default: null },
    destTankX: { type: Number, required: true },
    DEST_Y: { type: Number, required: true },
    slotLayout: { type: Array, required: true },
    lastAssignedSlot: { type: String, default: null }
  },
  emits: ["select-slot", "clear-slot", "clear-last-assigned"]
};
</script>

<style scoped>
@keyframes flowRight {
  to {
    stroke-dashoffset: -56;
  }
}

@keyframes flowUp {
  to {
    stroke-dashoffset: -56;
  }
}

.flow-dash {
  animation: flowRight 1.4s linear infinite;
}

.flow-dash--up {
  animation: flowUp 1.4s linear infinite;
}

@keyframes slotPopFlash {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.slot-pop-flash {
  animation: slotPopFlash 0.65s ease-out forwards;
}

.animations-paused .flow-dash,
.animations-paused .flow-dash--up,
.animations-paused .slot-pop-flash {
  animation-play-state: paused;
}
</style>
